// src/features/settings/LabelStyleEditor.jsx
// Shared editor for a map label style (spec §6). Controlled: parent owns the
// value and decides inherit vs custom. Pure UI; no store, no API.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { DEFAULT_LABEL_STYLE, LABEL_SHAPES, COLOR_MODES, MAX_RANGES, MIN_ZOOM_FLOOR, MIN_ZOOM_CEIL, TEXT_SIZES, TEXT_SIZE_PX, CROWDING_MODES, normalizeLabelStyle } from '@/features/map/labelStyle';
import { labelFieldCatalog, fieldLabel } from '@/features/map/labelFields';
import { CARD_FLEET_CAP } from '@/features/map/labelCards';
import { STATUS_CONFIG } from '@/lib/utils';

const inputClass = 'w-full h-9 px-3 text-xs rounded-xl border border-slate-200 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/10 bg-white';
const labelClass = 'block text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1';

const DEFAULT_VARIABLE = { key: 'speed', ranges: [{ to: 50, color: '#f59e0b' }, { to: null, color: '#ef4444' }], noDataColor: '#94a3b8' };

export function defaultEditableStyle() {
  return { ...DEFAULT_LABEL_STYLE, shape: 'pill', fields: ['speed'] };
}

function shapeLabel(shape, t) {
  switch (shape) {
    case 'pill': return t('mapLabels.shapePill', 'Pill');
    case 'rect': return t('mapLabels.shapeRect', 'Rectangle');
    default: return t('mapLabels.shapeNone', 'Text only');
  }
}
function crowdingLabel(mode, t) {
  switch (mode) {
    case 'stack': return t('mapLabels.crowding.stack', 'Stack as cards');
    case 'group': return t('mapLabels.crowding.group', 'Group into a count');
    default: return t('mapLabels.crowding.spread', 'Spread out');
  }
}

function crowdingHelp(mode, t) {
  switch (mode) {
    case 'stack': return t('mapLabels.crowding.stackHelp', 'Every label stays visible as a card; the top card is readable. Above {{cap}} vehicles the map falls back to Spread out', { cap: CARD_FLEET_CAP });
    case 'group': return t('mapLabels.crowding.groupHelp', 'Vehicles close together become a count at every zoom; tap it to fan them out');
    default: return t('mapLabels.crowding.spreadHelp', 'Labels move to another side of the marker; hidden only when every side is taken');
  }
}

function textSizeLabel(size, t) {
  switch (size) {
    case 'small': return t('mapLabels.textSizeSmall', 'Small');
    case 'large': return t('mapLabels.textSizeLarge', 'Large');
    case 'xlarge': return t('mapLabels.textSizeXLarge', 'Extra large');
    default: return t('mapLabels.textSizeNormal', 'Normal');
  }
}
function modeLabel(mode, t) {
  switch (mode) {
    case 'status': return t('mapLabels.modeStatus', 'By vehicle status');
    case 'variable': return t('mapLabels.modeVariable', 'By value');
    default: return t('mapLabels.modeFixed', 'One colour');
  }
}

function Preview({ style, t, device }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // The map draws a 9-slice canvas image; the preview uses the same colour,
    // radius and opacity in CSS so it never ends up behind the card background
    // (a negative z-index did exactly that) and never distorts the corners.
    const bg = style.colorMode === 'status' ? STATUS_CONFIG.moving.color : style.bg;
    el.style.background = style.shape === 'none' ? 'transparent' : bg;
    el.style.opacity = String(style.bgOpacity);
    el.style.borderRadius = style.shape === 'pill' ? '999px' : '4px';
  }, [style.shape, style.bg, style.bgOpacity, style.colorMode]);
  const name = t('mapLabels.previewName', 'Truck 12');
  const values = style.shape !== 'none' && style.fields.length
    ? style.fields.map((key) => (style.showFieldLabels === false ? '12' : `${fieldLabel(key, { device, t })} 12`)).join(' · ')
    : '';
  // Same rule as buildLabelProps: single line joins name + values with ' · '.
  const line1 = values && style.singleLine ? `${name} · ${values}` : name;
  const line2 = values && !style.singleLine ? values : '';
  const tight = style.shape !== 'none' && style.compact;
  const px = (TEXT_SIZE_PX[style.textSize] || TEXT_SIZE_PX.normal) - (tight ? 1 : 0);
  return (
    <div className="inline-flex flex-col items-center" aria-hidden="true">
      <div data-testid="label-preview-text" className={`relative font-bold leading-tight whitespace-nowrap ${tight ? 'px-1 py-px' : 'px-3 py-1'}`} style={{ fontSize: `${px}px`, color: style.shape === 'none' ? '#334155' : style.fg, textShadow: style.shape === 'none' ? '0 0 2px #fff' : 'none' }}>
        <div ref={ref} data-testid="label-preview-badge" className="absolute inset-0 overflow-hidden" />
        <div className="relative" data-testid="label-preview-line1"><bdi>{line1}</bdi></div>
        {line2 && <div className="relative font-semibold" data-testid="label-preview-line2"><bdi>{line2}</bdi></div>}
      </div>
    </div>
  );
}

export default function LabelStyleEditor({ value, onChange, device, rawAttributeKeys = [], showCrowding = false }) {
  const { t } = useTranslation();
  const style = useMemo(() => normalizeLabelStyle(value) || defaultEditableStyle(), [value]);
  const catalog = useMemo(() => labelFieldCatalog({ device, t }), [device, t]);
  const options = useMemo(() => [
    ...catalog,
    ...[...new Set([...rawAttributeKeys, ...style.fields.filter((f) => f.startsWith('attr:')).map((f) => f.slice(5))])]
      .filter((k) => !catalog.some((c) => c.key === `attr:${k}`))
      .map((k) => ({ key: `attr:${k}`, label: k, kind: 'number', group: 'raw' })),
  ], [catalog, rawAttributeKeys, style.fields]);
  const [attrText, setAttrText] = useState('');
  const [draft, setDraft] = useState({});
  const varKey = style.variable?.key;
  useEffect(() => { setDraft({}); }, [varKey]);
  const set = (patch) => onChange(normalizeLabelStyle({ ...style, ...patch }) || defaultEditableStyle());
  const setVariable = (patch) => set({ variable: { ...(style.variable || DEFAULT_VARIABLE), ...patch } });

  const setField = (idx, key) => {
    const next = [...style.fields];
    if (key) next[idx] = key; else next.splice(idx, 1);
    set({ fields: [...new Set(next.filter(Boolean))].slice(0, 2) });
  };

  const applyAttr = () => {
    const text = attrText.trim();
    if (!text) return;
    setField(!style.fields[0] ? 0 : !style.fields[1] ? 1 : 1, `attr:${text}`);
    setAttrText('');
  };

  const selectedVar = style.variable ? options.find((o) => o.key === style.variable.key) : null;
  const isBool = selectedVar?.kind === 'boolean';

  const onVariableKey = (key) => {
    const def = options.find((o) => o.key === key);
    if (def?.kind === 'boolean') setVariable({ key, ranges: [], onColor: style.variable?.onColor || '#10b981', offColor: style.variable?.offColor || '#64748b' });
    else {
      const { onColor, offColor, ...rest } = style.variable || {};
      set({ variable: { ...rest, key, ranges: rest.ranges?.length ? rest.ranges : [{ to: null, color: '#ef4444' }], noDataColor: rest.noDataColor || '#94a3b8' } });
    }
  };

  const ranges = style.variable?.ranges || [];
  const setRange = (i, patch) => setVariable({ ranges: ranges.map((r, j) => (j === i ? { ...r, ...patch } : r)) });
  const commitRange = (i) => {
    if (draft[i] === undefined) return;
    const n = draft[i] === '' ? NaN : Number(draft[i]);
    setDraft((d) => { const { [i]: _drop, ...rest } = d; return rest; });
    if (Number.isFinite(n)) setRange(i, { to: n });
  };
  const addRange = () => {
    if (ranges.length >= MAX_RANGES) return;
    const last = ranges[ranges.length - 1];
    const prevTo = ranges.length >= 2 ? ranges[ranges.length - 2].to : 0;
    setVariable({ ranges: [...ranges.slice(0, -1), { to: (prevTo || 0) + 10, color: '#f59e0b' }, last || { to: null, color: '#ef4444' }] });
  };
  const removeRange = (i) => { if (ranges.length > 1) setVariable({ ranges: ranges.filter((_, j) => j !== i) }); };

  const unitFor = (key) => (key === 'speed' ? 'km/h' : key === 'fuel' ? t('mapLabels.fuelUnitHint', 'L or %') : options.find((o) => o.key === key)?.unit || '');

  const fieldSelect = (idx) => (
    <select data-testid={`label-field-${idx}`} value={style.fields[idx] || ''} onChange={(e) => setField(idx, e.target.value)} className={inputClass}>
      <option value="">{t('mapLabels.fieldNone', 'None')}</option>
      <optgroup label={t('mapLabels.groupTelemetry', 'Telemetry')}>
        {options.filter((o) => o.group === 'telemetry').map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
      </optgroup>
      {options.some((o) => o.group === 'sensor') && (
        <optgroup label={t('mapLabels.groupSensors', 'Sensors')}>
          {options.filter((o) => o.group === 'sensor').map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
        </optgroup>
      )}
      {options.some((o) => o.group === 'raw') && (
        <optgroup label={t('mapLabels.groupRaw', 'Raw attributes')}>
          {options.filter((o) => o.group === 'raw').map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
        </optgroup>
      )}
    </select>
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-4">
        <Preview style={style} t={t} device={device} />
        <p className="text-[10px] text-slate-400">{t('mapLabels.previewHint', 'Preview of the label as it appears on the Live Map from zoom 12.')}</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <div>
          <label className={labelClass}>{t('mapLabels.shape', 'Badge')}</label>
          <select data-testid="label-shape" value={style.shape} onChange={(e) => set({ shape: e.target.value })} className={inputClass}>
            {LABEL_SHAPES.map((s) => <option key={s} value={s}>{shapeLabel(s, t)}</option>)}
          </select>
        </div>
        <div>
          <label className={labelClass}>{t('mapLabels.textColor', 'Text colour')}</label>
          <input type="color" value={style.fg} onChange={(e) => set({ fg: e.target.value })} className="h-9 w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white" />
        </div>
        <div>
          <label className={labelClass}>{t('mapLabels.textSize', 'Text size')}</label>
          <select data-testid="label-text-size" value={style.textSize} onChange={(e) => set({ textSize: e.target.value })} className={inputClass}>
            {TEXT_SIZES.map((s) => <option key={s} value={s}>{textSizeLabel(s, t)}</option>)}
          </select>
        </div>
        {style.shape !== 'none' && style.colorMode === 'fixed' && (
          <div>
            <label className={labelClass}>{t('mapLabels.badgeColor', 'Badge colour')}</label>
            <input type="color" value={style.bg} onChange={(e) => set({ bg: e.target.value })} className="h-9 w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white" />
          </div>
        )}
        {style.shape !== 'none' && (
          <div>
            <label className={labelClass}>{t('mapLabels.opacity', 'Badge opacity')} <bdi>{Math.round(style.bgOpacity * 100)}%</bdi></label>
            <input type="range" min={20} max={100} step={5} value={Math.round(style.bgOpacity * 100)} onChange={(e) => set({ bgOpacity: Number(e.target.value) / 100 })} className="w-full h-9" />
          </div>
        )}
      </div>
      {style.shape !== 'none' && (
        <div className="space-y-2">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>{t('mapLabels.field1', 'Value 1')}</label>
            {fieldSelect(0)}
          </div>
          <div>
            <label className={labelClass}>{t('mapLabels.field2', 'Value 2')}</label>
            {fieldSelect(1)}
          </div>
        </div>
          <div>
            <label className={labelClass}>{t('mapLabels.otherAttribute', 'Other attribute')}</label>
            <div className="flex items-center gap-2">
              <input data-testid="label-attr-input" list="label-attr-keys" value={attrText} placeholder={t('mapLabels.otherAttributeHint', 'Any key the tracker sends, e.g. adc1')} onChange={(e) => setAttrText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyAttr(); } }} className={inputClass} />
              <datalist id="label-attr-keys">
                {rawAttributeKeys.map((k) => <option key={k} value={k} />)}
              </datalist>
              <button type="button" data-testid="label-attr-use" onClick={applyAttr} className="h-9 px-3 text-xs font-semibold rounded-xl text-blue-600 bg-blue-50 hover:bg-blue-100">{t('mapLabels.use', 'Use')}</button>
            </div>
          </div>
        </div>
      )}
      {style.shape !== 'none' && (
        <div className="space-y-2">
          <div>
            <label className={labelClass}>{t('mapLabels.colorMode', 'Badge colour follows')}</label>
            <select data-testid="color-mode" value={style.colorMode} onChange={(e) => set(e.target.value === 'variable' ? { colorMode: 'variable', variable: style.variable || DEFAULT_VARIABLE } : { colorMode: e.target.value })} className={inputClass}>
              {COLOR_MODES.map((m) => <option key={m} value={m}>{modeLabel(m, t)}</option>)}
            </select>
          </div>
          {style.colorMode === 'variable' && (
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3 space-y-2">
              <div>
                <label className={labelClass}>{t('mapLabels.variable', 'Value')}</label>
                <select data-testid="variable-key" value={style.variable?.key || ''} onChange={(e) => onVariableKey(e.target.value)} className={inputClass}>
                  {options.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                </select>
              </div>
              {isBool ? (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelClass}>{t('mapLabels.onColor', 'When on')}</label>
                    <input data-testid="bool-on-color" type="color" value={style.variable.onColor} onChange={(e) => setVariable({ onColor: e.target.value })} className="h-9 w-full rounded-xl border bg-white" />
                  </div>
                  <div>
                    <label className={labelClass}>{t('mapLabels.offColor', 'When off')}</label>
                    <input type="color" value={style.variable.offColor} onChange={(e) => setVariable({ offColor: e.target.value })} className="h-9 w-full rounded-xl border bg-white" />
                  </div>
                </div>
              ) : (
                <div className="space-y-1.5">
                  {ranges.map((r, i) => (
                    <div key={i} data-testid={`range-row-${i}`} className="flex items-center gap-2">
                      {r.to === null ? (
                        <span className="text-xs text-slate-600 dark:text-slate-300 flex-1">{t('mapLabels.rangeAbove', 'Above that')}</span>
                      ) : (
                        <>
                          <span className="text-xs text-slate-600 dark:text-slate-300">{t('mapLabels.rangeUpTo', 'Up to')}</span>
                          <input type="number" value={draft[i] ?? r.to} onChange={(e) => setDraft((d) => ({ ...d, [i]: e.target.value }))} onBlur={() => commitRange(i)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitRange(i); } }} className={`${inputClass} w-24`} />
                          <span className="text-xs text-slate-400"><bdi>{unitFor(style.variable.key)}</bdi></span>
                        </>
                      )}
                      <input type="color" value={r.color} onChange={(e) => setRange(i, { color: e.target.value })} className="h-9 w-12 rounded-xl border bg-white" />
                      {r.to !== null && (
                        <button type="button" onClick={() => removeRange(i)} className="p-1.5 rounded-lg text-slate-400 hover:text-red-500" title={t('common.remove', 'Remove')}><Trash2 size={13} /></button>
                      )}
                    </div>
                  ))}
                  {ranges.length < MAX_RANGES && (
                    <button type="button" data-testid="range-add" onClick={addRange} className="h-8 px-3 text-xs font-semibold rounded-xl text-blue-600 bg-blue-50 hover:bg-blue-100 flex items-center gap-1">
                      <Plus size={12} /> {t('mapLabels.rangeAdd', 'Add range')}
                    </button>
                  )}
                </div>
              )}
              <div>
                <label className={labelClass}>{t('mapLabels.noDataColor', 'No data / stale')}</label>
                <input type="color" value={style.variable?.noDataColor || '#94a3b8'} onChange={(e) => setVariable({ noDataColor: e.target.value })} className="h-9 w-12 rounded-xl border bg-white" />
              </div>
            </div>
          )}
          {showCrowding && (
            <div>
              <label className={labelClass}>{t('mapLabels.crowding.title', 'When labels crowd')}</label>
              <div data-testid="label-crowding" role="group" className="inline-flex flex-wrap gap-1.5">
                {CROWDING_MODES.map((m) => (
                  <button
                    key={m}
                    type="button"
                    data-value={m}
                    aria-pressed={style.crowding === m}
                    onClick={() => set({ crowding: m, allowOverlap: m === 'stack' })}
                    className={`h-8 px-3 text-xs font-semibold rounded-xl border transition-colors ${style.crowding === m ? 'bg-blue-600 text-white border-blue-600' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'}`}
                  >
                    {crowdingLabel(m, t)}
                  </button>
                ))}
              </div>
              <p className="mt-1 text-[10px] text-slate-400 dark:text-slate-500">{crowdingHelp(style.crowding, t)}</p>
              <label className="mt-3 flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                <input type="checkbox" data-testid="label-click-selects" checked={style.clickSelects} onChange={(e) => set({ clickSelects: e.target.checked })} />
                {t('mapLabels.clickSelects', 'Clicking a label selects the vehicle, like clicking its marker')}
              </label>
            </div>
          )}
          {style.shape !== 'none' && (
            <>
              <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                <input type="checkbox" data-testid="label-glow" checked={style.selectionGlow} onChange={(e) => set({ selectionGlow: e.target.checked })} />
                {t('mapLabels.selectionGlow', 'Glow around the badge when the vehicle is selected')}
              </label>
              <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                <input type="checkbox" data-testid="label-shrink" checked={style.shrinkWhenZoomedOut} onChange={(e) => set({ shrinkWhenZoomedOut: e.target.checked })} />
                {t('mapLabels.shrinkZoomedOut', 'Shrink the badge when zoomed out')}
              </label>
              <div className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                <span>{t('mapLabels.minZoom', 'Show labels from zoom level')}</span>
                <select data-testid="label-min-zoom" value={style.minZoom} onChange={(e) => set({ minZoom: Number(e.target.value) })} className="h-8 px-2 rounded-xl border border-slate-200 dark:border-slate-700 dark:bg-slate-800 bg-white">
                  {Array.from({ length: MIN_ZOOM_CEIL - MIN_ZOOM_FLOOR + 1 }, (_, i) => MIN_ZOOM_FLOOR + i).map((z) => <option key={z} value={z}>{z}</option>)}
                </select>
                <span className="text-[10px] text-slate-400">{t('mapLabels.minZoomHint', '12 = streets (today\u2019s behaviour); lower numbers keep labels visible further out')}</span>
              </div>
              <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                <input type="checkbox" data-testid="label-value-names" checked={style.showFieldLabels !== false} onChange={(e) => set({ showFieldLabels: e.target.checked })} />
                {t('mapLabels.showFieldLabels', 'Show value names (e.g. ‘Speed 20 km/h’ instead of ‘20 km/h’)')}
              </label>
              <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                <input type="checkbox" data-testid="label-single-line" checked={style.singleLine} onChange={(e) => set({ singleLine: e.target.checked })} />
                {t('mapLabels.singleLine', 'Values on the same line as the name')}
              </label>
              <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                <input type="checkbox" data-testid="label-compact" checked={style.compact} onChange={(e) => set({ compact: e.target.checked })} />
                {t('mapLabels.compact', 'Compact badge (little padding, smaller text)')}
              </label>
            </>
          )}
        </div>
      )}
    </div>
  );
}
